/**
 * Project API functions
 */

import { Project, ProjectListResponse } from '../types/project';
import { STRAPI_URL } from './base';

/**
 * Fetch projects with optional pagination and filters
 */
export async function fetchProjects(params?: {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  isFeatured?: boolean;
}): Promise<ProjectListResponse> {
  try {
    const searchParams = new URLSearchParams();

    // Pagination
    if (params?.page) {
      searchParams.append('pagination[page]', params.page.toString());
    }
    if (params?.pageSize) {
      searchParams.append('pagination[pageSize]', params.pageSize.toString());
    }

    // Filters
    if (params?.isActive !== undefined) {
      searchParams.append('filters[isActive][$eq]', params.isActive.toString());
    }
    if (params?.isFeatured !== undefined) {
      searchParams.append(
        'filters[isFeatured][$eq]',
        params.isFeatured.toString()
      );
    }

    // Population
    searchParams.append('populate[image]', 'true');

    // Sorting
    searchParams.append('sort[0]', 'order:asc');
    searchParams.append('sort[1]', 'createdAt:desc');

    const url = `${STRAPI_URL}/api/projects?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 60 },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Projects API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch projects: ${response.status} - ${errorText}`
      );
    }

    const result: ProjectListResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
}

/**
 * Fetch featured projects (isFeatured = true)
 */
export async function fetchFeaturedProjects(
  limit: number = 6
): Promise<ProjectListResponse> {
  return fetchProjects({ isActive: true, isFeatured: true, pageSize: limit });
}

/**
 * Fetch a single project by slug
 */
export async function fetchProjectBySlug(
  slug: string
): Promise<{ data: Project }> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('filters[slug][$eq]', slug);
    searchParams.append('populate[image]', 'true');
    searchParams.append('populate[seo]', 'true');

    const url = `${STRAPI_URL}/api/projects?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 60 },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Project API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch project: ${response.status} - ${errorText}`
      );
    }

    const result: ProjectListResponse = await response.json();

    if (result.data.length === 0) {
      throw new Error('Project not found');
    }

    return { data: result.data[0] };
  } catch (error) {
    console.error('Error fetching project by slug:', error);
    throw error;
  }
}
